import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FaIcon } from 'client/js/util/layout_utils';
import LoadingTableSpinner from 'client/js/util/loading_table_spinner';
import Callout from 'client/js/util/callout';
import PropTypes from 'prop-types';

// Separate LogItem component to handle border classes properly
const LogItem = ({ item, isNew, isExpanded, onToggleExpanded, renderLogItem, renderLogDetails }) => {
  // Get border class based on log type
  const getBorderClass = () => {
    // For access logs (has status field)
    if (item.status) {
      const suffix = item.status >= 200 && item.status < 300 ? '2xx' :
                     item.status >= 300 && item.status < 400 ? '3xx' :
                     item.status >= 400 && item.status < 500 ? '4xx' :
                     item.status >= 500 ? '5xx' : 'unknown';
      return `border-status-${suffix}`;
    }
    
    // For Apache error logs (has log_level field)
    if (item.log_level) {
      const lowerLevel = item.log_level.toLowerCase();
      switch (lowerLevel) {
        case 'emergency':
        case 'alert':
        case 'critical':
          return 'border-log-critical';
        case 'error':
          return 'border-log-error';
        case 'warning':
          return 'border-log-warning';
        case 'notice':
        case 'info':
          return 'border-log-info';
        case 'debug':
          return 'border-log-debug';
        default:
          return 'border-log-unknown';
      }
    }
    
    // For PHP error logs (has level field)
    if (item.level) {
      switch (item.level) {
        case 'fatal_error':
        case 'parse_error':
          return 'border-log-critical';
        case 'error':
          return 'border-log-error';
        case 'warning':
          return 'border-log-warning';
        case 'deprecated':
          return 'border-log-info';
        default:
          return 'border-log-unknown';
      }
    }
    
    return 'border-log-unknown';
  };

  return (
    <div
      className={`log-item ${getBorderClass()} ${isNew ? 'highlight-new' : ''}`}
    >
      <div
        className="log-header"
        role="button"
        tabIndex={0}
        onClick={onToggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpanded();
          }
        }}
      >
        <span className={`log-chevron ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </span>
        {renderLogItem(item)}
      </div>

      {isExpanded && renderLogDetails && (
        <div className="log-details">
          <div className="log-details-grid">
            {renderLogDetails(item)}
          </div>
        </div>
      )}
    </div>
  );
};

LogItem.propTypes = {
  item: PropTypes.object.isRequired,
  isNew: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  renderLogItem: PropTypes.func.isRequired,
  renderLogDetails: PropTypes.func
};

// Common log display component for access logs and error logs
const LogDisplay = ({
  endpoint,
  queryKey,
  filters,
  onFilterChange,
  validateFilters,
  clearFilters,
  renderLogItem,
  renderLogDetails,
  NoDataMessage = () => (
    <Callout calloutClass="info" title="Keine Logs gefunden">
      <p>Für die aktuellen Filter wurden keine Logs gefunden. Versuchen Sie andere Filterkriterien oder warten Sie auf neue Logs.</p>
    </Callout>
  ),
  FilterSection = null,
  refreshInterval = 3000
}) => {
  const [newItems, setNewItems] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isPaused, setIsPaused] = useState(false);
  const [hasSubscriptionError, setHasSubscriptionError] = useState(false);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [filterErrors, setFilterErrors] = useState({});
  const previousIds = useRef(new Set());

  // Build URL with query parameters
  const buildURL = (filters) => {
    const url = new URL(endpoint, window.location.origin);
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim && value.trim()) {
        url.searchParams.set(key, value);
      }
    });
    return url.pathname + url.search;
  };

  // Debounce filters to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Validate filters when they change
  useEffect(() => {
    if (validateFilters) {
      const validation = validateFilters(filters);
      setFilterErrors(validation.errors);
    }
  }, [filters, validateFilters]);

  // Check if there are any client-side validation errors
  const hasValidationErrors = Object.keys(filterErrors).length > 0;

  const { data, isLoading, isRefetching, error } = useQuery(
    [queryKey, debouncedFilters],
    async () => {
      try {
        const response = await $.ajax({
          url: buildURL(debouncedFilters),
          dataType: 'json'
        });
        return response;
      } catch (jqXHR) {
        const errorObj = {
          status: jqXHR.status,
          statusText: jqXHR.statusText,
          message: jqXHR.responseJSON?.error || jqXHR.responseText || 'Unknown error'
        };
        throw errorObj;
      }
    },
    {
      enabled: !hasValidationErrors,
      keepPreviousData: true,
      refetchInterval: isPaused || hasValidationErrors || hasSubscriptionError ? false : refreshInterval,
      retry: (failureCount, error) => {
        if (error?.status === 400 || error?.status === 402) {
          return false;
        }
        return failureCount < 3;
      }
    }
  );

  // Monitor error state to detect subscription issues
  useEffect(() => {
    setHasSubscriptionError(error?.status === 402);
  }, [error]);

  const handleFilterChange = (field, value) => {
    onFilterChange(field, value);
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Track new items for highlighting
  useEffect(() => {
    if (!data?.logs) return;

    const currentIds = new Set(data.logs.map(item => item.id));
    const newIds = new Set();

    currentIds.forEach(id => {
      if (!previousIds.current.has(id)) {
        newIds.add(id);
      }
    });

    if (newIds.size > 0) {
      setNewItems(newIds);
      setTimeout(() => {
        setNewItems(new Set());
      }, 2000);
    }

    previousIds.current = currentIds;
  }, [data?.logs]);

  // Handle loading state
  if (isLoading) {
    return <LoadingTableSpinner />;
  }

  const isServerValidationError = error?.status === 400;

  // Handle subscription errors
  if (hasSubscriptionError) {
    return (
      <Callout
        calloutClass="danger"
        title="Feature nicht verfügbar"
      >
        <p>
          Auf Deinem Account ist derzeit kein Webhosting-Paket vorhanden. Bitte füge ein Webhosting-Paket unter &quot;Webhosting-Paket&quot; hinzu.
        </p>
      </Callout>
    );
  }

  // Handle non-validation errors - but still show filters so users can fix them
  const hasNonValidationError = error && !isServerValidationError;

  const logs = data?.logs || [];

  return (
    <>

      {/* Show server validation errors */}
      {!hasValidationErrors && isServerValidationError && (
        <div className="alert alert-danger">
          <strong>Server-Validierungsfehler:</strong> {error.message}
        </div>
      )}

      {/* Show non-validation server errors */}
      {hasNonValidationError && (
        <div className="alert alert-danger">
          <strong>Fehler beim Laden der Logs:</strong> {error?.statusText || error?.message || 'Unbekannter Fehler'}
        </div>
      )}

      {/* Only show filters and content if user has subscription access */}
      {!hasSubscriptionError && (
        <>
          {FilterSection && (
            <div className="filter-section">
              <div className="filter-row">
                <FilterSection
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  filterErrors={filterErrors}
                />
                <div className="filter-buttons">
                  {(isLoading || isRefetching) && <span className="css-spinloader"></span>}
                  <button
                    className="btn btn-secondary"
                    onClick={clearFilters}
                  >
                    Filter zurücksetzen
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsPaused(!isPaused)}
                    title={isPaused ? 'Automatische Aktualisierung fortsetzen' : 'Automatische Aktualisierung pausieren'}
                  >
                    {isPaused ? (
                      <>
                        <FaIcon name="play" /> Fortsetzen
                      </>
                    ) : (
                      <>
                        <FaIcon name="pause" /> Pausieren
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!hasValidationErrors && !hasNonValidationError && !isServerValidationError && !logs.length && (
            <NoDataMessage />
          )}

          {!hasValidationErrors && !hasNonValidationError && !isServerValidationError && logs.length > 0 && (
            <div className="log-container">
              {logs.map((item) => {
                const isNew = newItems.has(item.id);
                const isExpanded = expandedItems.has(item.id);

                return (
                  <LogItem
                    key={item.id}
                    item={item}
                    isNew={isNew}
                    isExpanded={isExpanded}
                    onToggleExpanded={() => toggleExpanded(item.id)}
                    renderLogItem={renderLogItem}
                    renderLogDetails={renderLogDetails}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

LogDisplay.propTypes = {
  endpoint: PropTypes.string.isRequired,
  queryKey: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  validateFilters: PropTypes.func,
  clearFilters: PropTypes.func.isRequired,
  renderLogItem: PropTypes.func.isRequired,
  renderLogDetails: PropTypes.func,
  NoDataMessage: PropTypes.func,
  FilterSection: PropTypes.func,
  refreshInterval: PropTypes.number
};

export default LogDisplay;